import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import {
	IActuateBarrierRequestData,
	IAddEventVisitrRequestData,
	IAddVisitorRequestData,
	IGetDashboardDetailsRequestData,
	IGetEventFrameRequestData,
	IGetEventFrameResponseData,
	IGetEventsListRequestData,
	IGetEventsListResponse,
	IGetFrameImageRequestData,
	IGetFrameImageResponseData,
	IGetLineChartResponseData,
	IGetUptimeChartResponseData,
	IGetUptimeStatsResponseData,
	IGetUserSettingsResponseData,
	IGetVehicleAuthChartResponseData,
	IGetVehicleMetricsResponseData,
	IGetVehicleTypesChartResponseData,
	IUpdateUserSettingsRequestData
} from "../../interfaces";
import { queries } from "./queries.reducer";
import { RootState } from "..";
import { addCachedImageData, updateCachedImageTimestamp } from "./auth.reducer";

const dashboard = queries.injectEndpoints({
	endpoints: (builder) => ({
		// GET LINE CHART DATA
		getLineChartData: builder.query<IGetLineChartResponseData, IGetDashboardDetailsRequestData>({
			query: (requestData) => ({
				url: "/analytics/dashboard/linechart",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET VEHICLE METRICS
		getVehicleMetrics: builder.query<IGetVehicleMetricsResponseData, IGetDashboardDetailsRequestData>({
			query: (requestData) => ({
				url: "/analytics/numberplatevent/get_dashboard_metric_data",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET EVENTS LIST
		getEventsList: builder.query<IGetEventsListResponse, IGetEventsListRequestData>({
			query: (requestData) => ({
				url: "/analytics/events",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET EVENT EVIDENCE IMAGE
		getEventFrame: builder.query<IGetEventFrameResponseData, IGetEventFrameRequestData>({
			query: (requestData) => ({
				url: `/analytics/numberplatevent/${requestData.id}/download_numberplateevent_image`,
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData.data
			})
		}),

		// ADD NEW VISITOR
		addVisitor: builder.mutation<void, IAddVisitorRequestData>({
			query: (requestData) => ({
				url: "/analytics/manualnumberplate",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// ACTUATE BARRIER
		actuateBarrier: builder.mutation<void, IActuateBarrierRequestData>({
			query: (requestData) => ({
				url: "/analytics/barrier",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET UPTIME STATS
		getUptimeStats: builder.query<IGetUptimeStatsResponseData, IGetDashboardDetailsRequestData>({
			query: (requestData) => ({
				url: "/settings/get_uptime_stats",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// ADD EVENT VISITOR
		addEventVisitor: builder.mutation<void, IAddEventVisitrRequestData>({
			query: (requestData) => ({
				url: `/analytics/manualnumberplate/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET UPTIME CHART DATA
		getUptimeChartData: builder.query<IGetUptimeChartResponseData[], IGetDashboardDetailsRequestData>({
			query: (requestData) => ({
				url: "/settings/get_consolidated_uptime_logs",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET VEHICLE AUTH CHART DATA
		getVehicleAuthChartData: builder.query<IGetVehicleAuthChartResponseData, IGetDashboardDetailsRequestData>({
			query: (requestData) => ({
				url: "/analytics/numberplatevent/get_line_chart_vehicle_auth_data",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET VEHICLE TYPES CHART DATA
		getVehicleTypesChartData: builder.query<IGetVehicleTypesChartResponseData, IGetDashboardDetailsRequestData>({
			query: (requestData) => ({
				url: "/analytics/numberplatevent/get_line_chart_vehicle_type_data",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET USER SETTINGS
		getUserSettings: builder.query<IGetUserSettingsResponseData, null>({
			query: () => ({
				url: "/auth/auth/get_user_settings/",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// UPDATE USER SETTINGS
		updateUserSettings: builder.mutation<unknown, IUpdateUserSettingsRequestData>({
			query: (requestData) => ({
				url: "/auth/auth/update_user_settings/",
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET FRAME IMAGE
		getFrameImage: builder.mutation<IGetFrameImageResponseData, IGetFrameImageRequestData>({
			queryFn: async (requestData, api, _extraOptions, baseQuery) => {
				const cacheEntryKey = `${requestData.image_type}|${requestData.message_id}`;

				const cachedImages = (api.getState() as RootState).auth.cachedImagesMap;

				if (cachedImages[cacheEntryKey]) {
					api.dispatch(updateCachedImageTimestamp({ key: cacheEntryKey, timestamp: +new Date() }));
					return { data: cachedImages[cacheEntryKey].data };
				}

				try {
					const response = await baseQuery({
						url: "/cameraconfig/get_image",
						body: requestData,
						headers: getAPIHeaderOptions(true),
						method: "POST"
					});

					if (response.data) {
						const responseData = response.data as IGetFrameImageResponseData;

						api.dispatch(
							addCachedImageData({
								key: cacheEntryKey,
								data: responseData,
								timestamp: +new Date()
							})
						);

						return { data: responseData };
					}

					return { error: { error: "Something went wrong", status: "CUSTOM_ERROR" } };
				} catch (error) {
					return { error: { error: "Something went wrong", status: "CUSTOM_ERROR" } };
				}
			}
		})
	})
});

export const {
	useGetLineChartDataQuery,
	useGetVehicleMetricsQuery,
	useGetEventsListQuery,
	useLazyGetEventFrameQuery,
	useAddVisitorMutation,
	useActuateBarrierMutation,
	useGetUptimeStatsQuery,
	useAddEventVisitorMutation,
	useGetUptimeChartDataQuery,
	useGetVehicleAuthChartDataQuery,
	useGetVehicleTypesChartDataQuery,
	useGetUserSettingsQuery,
	useUpdateUserSettingsMutation,
	useGetFrameImageMutation
} = dashboard;

export default dashboard;
